/**
 * Floor plan — several rectangular rooms laid out on one sheet.
 *
 * A plan is what the Floor Planner edits and what the 3D designer shows. Each
 * room is a rectangle of INSIDE measurements at a position on the sheet, with
 * its own ceiling height and its own surfaces (the same `RoomSurfaces` the
 * designer has always edited: walls in the viewer's order [front, right, back,
 * left] plus the ceiling).
 *
 * Rooms are all square to each other (Rob, 2026-10-02 — angles may come later).
 * On the sheet +x runs along a room's length and +z along its width:
 *
 *   front wall  z = room.z              runs along x
 *   back wall   z = room.z + width      runs along x
 *   left wall   x = room.x              runs along z
 *   right wall  x = room.x + length     runs along z
 *
 * JOINED ROOMS. Two rooms are joined when facing walls sit exactly one stud
 * depth apart (`WALL_GAP_FT`) — that gap is the shared wall. The wall is framed
 * once, by the OLDER room (earlier in `rooms`), and has two faces: each room
 * panels its own side. Joins are never stored; `sharedStretches` works them out
 * from where the rooms are, so moving a room away simply un-joins it.
 *
 * Rooms never overlap, and are either joined or far enough apart for two
 * separate walls (`2 × WALL_GAP_FT`).
 *
 * Everything here is pure; every edit returns a new plan or the reason it was
 * refused, like the section edits.
 */

import type { Placement } from '../staging/kits';
import { RoomDimensions, SlatWallConfig, FullRoomWallConfig } from '../../types';
import { Opening, RoomSurfaces, Surface, SlatWallAccessoryKind, mirroredHanding } from '../../types/sections';
import { PlacementIssue, checkOpeningPlacement } from '../sections/validation';
import { nextOpeningId } from '../sections/openings';
import { createFullRoomWallConfigs } from '../dimensions';
import { roomSurfacesFromWallConfigs, ceilingSurface } from '../sections/generator';
import { snapValue } from '../sections/dragMath';
import { resizeSurface } from '../sections/resizeRoom';
import { copySectionsTo } from '../sections/copyLayout';
import { CeilingProfile, FLAT, WallFaceName, ceilingGeometry, profileIssue } from '../ceiling/profile';

/** The thickness of a wall on the plan: a 2x4 stud's depth. */
export const WALL_GAP_FT = 3.5 / 12;
/** How far the lawn reaches beyond the plan's bounds (feet; yard.ts): where the yard's props may stand. */
export const LAWN_MARGIN_FT = 40;
/** A new room on a blank plan. */
export const DEFAULT_ROOM_HEIGHT_FT = 9;
/** Feet. */
export const ROOM_LIMITS = {
  side: { min: 4, max: 100 },
  height: { min: 6, max: 24 },
} as const;

const EPS = 1e-4;
const INCH = 1 / 12;

export interface PlanRect {
  /** Front-left inside corner on the sheet, feet. */
  x: number;
  z: number;
  length: number;
  width: number;
}

export interface PlanRoom extends PlanRect {
  id: string;
  name: string;
  /** The WALL (eave) height. A ceiling profile rises above it. */
  height: number;
  surfaces: RoomSurfaces;
  /** The shape of the ceiling (absent = flat at the wall height). */
  ceilingProfile?: CeilingProfile;
}

/**
 * A pair of joined rooms whose shared wall has been taken out: all of the shared
 * stretch (no `from`), or just the part behind one window or door (`link` names
 * it; the opening stays where it is, and what is left if it is deleted is a
 * framed void).
 */
export interface OpenWall {
  a: string;
  b: string;
  /** Only this part of the shared stretch (sheet coordinates along the wall). */
  from?: number;
  to?: number;
  /** Only this band, up from the floor; absent = floor to the lower ceiling. */
  y?: number;
  height?: number;
  /** The `Opening.link` of the window or door the wall was taken out behind. */
  link?: string;
}

/**
 * What Stage drew for the render from the finishes by space (room3d/renderFinish.ts):
 * a floor texture set with its settings, an HDRI with its. Goes with the staging.
 */
export interface PlanFinish {
  /** A texture placement (room3d/photoRender.ts TexturePlacement); rotation and brightness absent on picks made before they existed. */
  floor?: { id: string; scale: number; roughness: number; relief: number; rotation?: number; brightness?: number };
  hdri?: { file: string; strength: number; rotation: number };
}

export function isPlanFinish(v: unknown): v is PlanFinish {
  const isNum = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);
  if (!v || typeof v !== 'object') return false;
  const f = v as PlanFinish;
  const fl = f.floor, h = f.hdri;
  return (fl === undefined || (!!fl && typeof fl === 'object' && typeof fl.id === 'string' && isNum(fl.scale) && isNum(fl.roughness) && isNum(fl.relief)
      && (fl.rotation === undefined || isNum(fl.rotation)) && (fl.brightness === undefined || isNum(fl.brightness))))
    && (h === undefined || (!!h && typeof h === 'object' && typeof h.file === 'string' && isNum(h.strength) && isNum(h.rotation)));
}

export interface FloorPlan {
  /** Oldest first: the older of two joined rooms owns (frames) the shared wall. */
  rooms: PlanRoom[];
  openWalls: OpenWall[];
  /** What the space is for (the estimator's space types: 'garage', 'laundry-room', …); picks the staging kit. */
  spaceType?: string;
  /** The props staging placed (calculations/staging); pruned as rooms change. */
  staging?: StagedProp[];
  /** The floor set and HDRI Stage drew for the render; goes when the staging goes. */
  finish?: PlanFinish;
}

/**
 * A staged prop, as calculations/staging places it (kept here for the plan's
 * own pruning). An `accessory` is a SlatWall accessory staging hung itself
 * (the user had none): `x`, `y` are its bottom-left on the wall (surface
 * coordinates), `accessoryKind` what it is. It goes with the staging and is
 * never counted. A `slatwall` prop hangs on an accessory — the user's, or a
 * staged one — named by `hostAccessoryId`; `x`, `y` are where it is held.
 */
export interface StagedProp {
  id: string;
  propId: string;
  roomId: string;
  /** `ceiling`: hung from the ceiling at room (x, z), `y` the height of the point that holds it. */
  /** `yard`: outside the house, in its room's coordinates (so outside 0..L / 0..W). */
  kind: 'floor' | 'slatwall' | 'wall' | 'accessory' | 'ceiling' | 'yard';
  /** How it was placed (staging/catalogue.ts): what the renderer anchors the model by. Absent in older plans. */
  place?: Placement;
  /**
   * Which of the prop's photoreal models (Prop Studio's library key: `bike`,
   * `bike~2`) this copy was staged for, when the library was there to ask:
   * the copy is staged at that model's size, the designer draws its preview,
   * and the render shows the same one. Absent: the kit's size, the
   * procedural prop, and the render's own draw.
   */
  variant?: string;
  x: number;
  z: number;
  /**
   * Floor props staged for a model that stands off the prop's origin (Prop
   * Studio's offset): where the model's box is centred, room coordinates.
   * The origin `x`, `z` is what the model is drawn from, and can lie outside
   * the room (behind the wall the prop stands against); the box never does.
   */
  body?: { x: number; z: number };
  /**
   * Floor props in line with a garage door (a car): which door, and how much
   * of the prop is in the room (staging/kits.ts `inside`). Below 1 the rest
   * stands outside through the door, and the room holds that door open; at
   * 0 all of it is on the driveway and the door stays shut.
   */
  bay?: { face: PlanFace; openingId: string; link?: string; inside: number };
  rotation: number;
  face?: PlanFace;
  y?: number;
  hostSectionId?: string;
  hostAccessoryId?: string;
  accessoryKind?: SlatWallAccessoryKind;
}

export type PlanFace = 'front' | 'right' | 'back' | 'left';
/** `RoomSurfaces.walls` order. */
export const PLAN_FACES: PlanFace[] = ['front', 'right', 'back', 'left'];

export type PlanIssue = 'overlap' | 'too-close' | 'too-small' | 'too-large' | 'not-found' | 'bad-profile';
/** Why an edit was refused: a plan rule, or a placement rule on a wall. */
export type PlanRefusal = PlanIssue | PlacementIssue | 'straddles-shared-wall' | 'other-room';
export type PlanResult = { ok: true; plan: FloorPlan } | { ok: false; reason: PlanRefusal };

export const EMPTY_PLAN: FloorPlan = { rooms: [], openWalls: [] };

export const roomDims = (room: PlanRoom): RoomDimensions => ({ length: room.length, width: room.width, height: room.height });
export const rectOfRoom = (room: PlanRect): PlanRect => ({ x: room.x, z: room.z, length: room.length, width: room.width });
export const findRoom = (plan: FloorPlan, id: string | null | undefined): PlanRoom | undefined => plan.rooms.find((r) => r.id === id);

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

/**
 * A stretch of wall along one face of a room, turned into that wall's own x
 * (the coordinate its sections and openings use). `from` / `to` are sheet
 * coordinates along the wall. Back and right walls are measured the other way
 * round — they are seen from inside the room.
 */
export function surfaceRange(room: PlanRect, face: PlanFace, from: number, to: number): [number, number] {
  switch (face) {
    case 'front': return [from - room.x, to - room.x];
    case 'back': return [room.length - (to - room.x), room.length - (from - room.x)];
    case 'left': return [from - room.z, to - room.z];
    case 'right': return [room.width - (to - room.z), room.width - (from - room.z)];
  }
}

/** The other way: a wall's own x → the sheet coordinate along that wall. */
export function sheetAlong(room: PlanRect, face: PlanFace, surfaceX: number): number {
  switch (face) {
    case 'front': return room.x + surfaceX;
    case 'back': return room.x + room.length - surfaceX;
    case 'left': return room.z + surfaceX;
    case 'right': return room.z + room.width - surfaceX;
  }
}

export interface SharedStretch {
  /** The older room: its framing is the wall. Range in that wall's own x. */
  owner: { roomId: string; face: PlanFace; from: number; to: number };
  other: { roomId: string; face: PlanFace; from: number; to: number };
  /** The direction the wall runs on the sheet. */
  axis: 'x' | 'z';
  /** Sheet range along `axis`, and where the middle of the wall is across it. */
  from: number;
  to: number;
  at: number;
}

/** Every stretch of wall two rooms share. */
export function sharedStretches(plan: FloorPlan): SharedStretch[] {
  const out: SharedStretch[] = [];
  const add = (a: PlanRoom, aFace: PlanFace, b: PlanRoom, bFace: PlanFace, axis: 'x' | 'z', at: number) => {
    const from = axis === 'x' ? Math.max(a.x, b.x) : Math.max(a.z, b.z);
    const to = axis === 'x' ? Math.min(a.x + a.length, b.x + b.length) : Math.min(a.z + a.width, b.z + b.width);
    if (to - from < INCH) return; // corners touching is not a shared wall
    const [af, at2] = surfaceRange(a, aFace, from, to);
    const [bf, bt] = surfaceRange(b, bFace, from, to);
    out.push({
      owner: { roomId: a.id, face: aFace, from: af, to: at2 },
      other: { roomId: b.id, face: bFace, from: bf, to: bt },
      axis, from, to, at,
    });
  };
  for (let i = 0; i < plan.rooms.length; i++) {
    for (let j = i + 1; j < plan.rooms.length; j++) {
      const a = plan.rooms[i];
      const b = plan.rooms[j];
      const near = (v: number) => Math.abs(v - WALL_GAP_FT) < EPS;
      if (near(b.x - (a.x + a.length))) add(a, 'right', b, 'left', 'z', a.x + a.length + WALL_GAP_FT / 2);
      else if (near(a.x - (b.x + b.length))) add(a, 'left', b, 'right', 'z', a.x - WALL_GAP_FT / 2);
      else if (near(b.z - (a.z + a.width))) add(a, 'back', b, 'front', 'x', a.z + a.width + WALL_GAP_FT / 2);
      else if (near(a.z - (b.z + b.width))) add(a, 'front', b, 'back', 'x', a.z - WALL_GAP_FT / 2);
    }
  }
  return out;
}

/** Part of a wall's framing that is not built (the wall's own x, feet). */
export interface FramingOmission {
  from: number;
  to: number;
  /** Only up to this height is left out; the room frames the rest itself. */
  below?: number;
}

/**
 * The framing a room does NOT build: every stretch of its walls shared with an
 * older room, whose wall it is. Where this room is the taller of the two, only
 * the part up to the older room's ceiling is left out.
 */
export function framingOmissions(plan: FloorPlan, roomId: string): Partial<Record<PlanFace, FramingOmission[]>> {
  const out: Partial<Record<PlanFace, FramingOmission[]>> = {};
  const room = findRoom(plan, roomId);
  if (!room) return out;
  for (const s of sharedStretches(plan)) {
    if (s.other.roomId !== roomId) continue;
    const owner = findRoom(plan, s.owner.roomId);
    if (!owner) continue;
    const omission: FramingOmission = { from: s.other.from, to: s.other.to };
    if (room.height > owner.height + EPS) omission.below = owner.height;
    (out[s.other.face] ??= []).push(omission);
  }
  return out;
}

/** Is this rectangle a sound room size? */
export function sizeIssue(rect: PlanRect): PlanIssue | null {
  const { min, max } = ROOM_LIMITS.side;
  if (rect.length < min - EPS || rect.width < min - EPS) return 'too-small';
  if (rect.length > max + EPS || rect.width > max + EPS) return 'too-large';
  return null;
}

/**
 * May a room occupy `rect`, given the other rooms? Null when it may.
 * Rooms never overlap; where two face each other they are either joined (one
 * wall between them) or at least two walls apart.
 */
export function placementIssue(rooms: PlanRect[], rect: PlanRect): PlanIssue | null {
  const size = sizeIssue(rect);
  if (size) return size;
  for (const other of rooms) {
    // Separation along each axis; negative = the two overlap along it.
    const dx = Math.max(other.x - (rect.x + rect.length), rect.x - (other.x + other.length));
    const dz = Math.max(other.z - (rect.z + rect.width), rect.z - (other.z + other.width));
    if (dx < -EPS && dz < -EPS) return 'overlap';
    const facing = dx < -EPS ? dz : dz < -EPS ? dx : null; // the gap between facing walls
    if (facing === null) continue; // diagonal neighbours
    if (facing < WALL_GAP_FT - EPS) return 'overlap'; // into the other room's wall
    if (Math.abs(facing - WALL_GAP_FT) > EPS && facing < 2 * WALL_GAP_FT - EPS) return 'too-close';
  }
  return null;
}

/** The sheet rectangle that holds every room (inside faces), or null for a blank plan. */
export function planBounds(plan: FloorPlan): PlanRect | null {
  if (plan.rooms.length === 0) return null;
  const x0 = Math.min(...plan.rooms.map((r) => r.x));
  const z0 = Math.min(...plan.rooms.map((r) => r.z));
  const x1 = Math.max(...plan.rooms.map((r) => r.x + r.length));
  const z1 = Math.max(...plan.rooms.map((r) => r.z + r.width));
  return { x: x0, z: z0, length: x1 - x0, width: z1 - z0 };
}

// ---------------------------------------------------------------------------
// Snapping
// ---------------------------------------------------------------------------

export interface PlanSnap {
  /** Edges land on multiples of this when nothing nearer catches them (feet). */
  grid: number;
  /** How near (feet) another room's line must be to catch an edge. */
  tol: number;
}

export interface SnapLines {
  xs: number[];
  zs: number[];
}

/**
 * The lines an edge may catch on: every other room's edges (to line up with
 * them) and, one wall's thickness out from each, where a room joins on.
 */
export function snapLinesFor(rooms: PlanRect[]): SnapLines {
  const xs: number[] = [];
  const zs: number[] = [];
  for (const r of rooms) {
    xs.push(r.x, r.x + r.length, r.x - WALL_GAP_FT, r.x + r.length + WALL_GAP_FT);
    zs.push(r.z, r.z + r.width, r.z - WALL_GAP_FT, r.z + r.width + WALL_GAP_FT);
  }
  return { xs, zs };
}

const toGrid = (v: number, grid: number) => (grid > 0 ? Math.round(v / grid) * grid : v);

/** One edge: a nearby line wins; otherwise the grid. */
function snapEdge(v: number, lines: number[], snap: PlanSnap): { value: number; caught: boolean } {
  const s = snapValue(v, lines, snap.tol);
  return s !== v ? { value: s, caught: true } : { value: toGrid(v, snap.grid), caught: false };
}

/** A single point (where a drawing starts): a nearby line, else the grid. */
export function snapPoint(x: number, z: number, lines: SnapLines, snap: PlanSnap): { x: number; z: number } {
  return { x: snapEdge(x, lines.xs, snap).value, z: snapEdge(z, lines.zs, snap).value };
}

/** A room being moved: it keeps its size and shifts so that one edge per axis lands. */
export function snapMove(rect: PlanRect, lines: SnapLines, snap: PlanSnap): PlanRect {
  const along = (start: number, size: number, targets: number[]) => {
    const lo = snapEdge(start, targets, snap);
    const hi = snapEdge(start + size, targets, snap);
    if (lo.caught && hi.caught) return Math.abs(lo.value - start) <= Math.abs(hi.value - start - size) ? lo.value : hi.value - size;
    if (lo.caught) return lo.value;
    if (hi.caught) return hi.value - size;
    return lo.value;
  };
  return { ...rect, x: along(rect.x, rect.length, lines.xs), z: along(rect.z, rect.width, lines.zs) };
}

export interface MovingEdges { left?: boolean; right?: boolean; front?: boolean; back?: boolean }

/**
 * A room being resized or drawn: each edge that is moving lands on its own. A
 * nearby line catches it; otherwise it settles where the room's SIZE is a
 * whole number of grid steps — so a room drawn from another room's wall comes
 * out 10' long, not 9' 8½".
 */
export function snapResize(rect: PlanRect, edges: MovingEdges, lines: SnapLines, snap: PlanSnap): PlanRect {
  const span = (start: number, end: number, movesStart: boolean | undefined, movesEnd: boolean | undefined, targets: number[]): [number, number] => {
    let a = start;
    let b = end;
    if (movesStart) { const s = snapValue(a, targets, snap.tol); a = s !== a ? s : b - toGrid(b - a, snap.grid); }
    if (movesEnd) { const s = snapValue(b, targets, snap.tol); b = s !== b ? s : a + toGrid(b - a, snap.grid); }
    return [a, b];
  };
  const [x0, x1] = span(rect.x, rect.x + rect.length, edges.left, edges.right, lines.xs);
  const [z0, z1] = span(rect.z, rect.z + rect.width, edges.front, edges.back, lines.zs);
  // (Tidied, so 34.2917 - 24.2917 is 10 and not 10.000000000000004.)
  const tidy = (v: number) => Math.round(v * 1e6) / 1e6;
  return { x: x0, z: z0, length: tidy(x1 - x0), width: tidy(z1 - z0) };
}

// ---------------------------------------------------------------------------
// Building rooms
// ---------------------------------------------------------------------------

const NO_SLATWALL: SlatWallConfig = { enabled: false, startHeight: 0, numPanelRows: 0, color: 'White', useCapTrim: false };
const NO_GARAGE: FullRoomWallConfig['garage'] = { enabled: false, width: 0, height: 0, numDoors: 0, onWallType: 'None' };

/** The surfaces of a room with nothing on them: four bare walls and a bare ceiling. */
export function bareSurfaces(dims: RoomDimensions): RoomSurfaces {
  const walls = createFullRoomWallConfigs(dims, { wallColor: 'White', wallOrientation: 'Horizontal', slatWall: NO_SLATWALL, garage: NO_GARAGE });
  const surfaces = roomSurfacesFromWallConfigs(walls, dims.height);
  return {
    walls: surfaces.walls.map((w) => ({ ...w, sections: [] })),
    ceiling: { ...ceilingSurface('ceiling', 'Ceiling', dims.length, dims.width, 'White'), sections: [] },
  };
}

/** A one-room plan from the designer's older single-room document. */
export function planFromSingleRoom(dims: RoomDimensions, surfaces: RoomSurfaces, name = 'Room 1'): FloorPlan {
  return { rooms: [{ id: 'room1', name, x: 0, z: 0, ...dims, surfaces }], openWalls: [] };
}

function nextNumber(plan: FloorPlan): number {
  const used = plan.rooms.map((r) => Number(/^room(\d+)$/.exec(r.id)?.[1] ?? 0));
  return Math.max(0, ...used) + 1;
}

/**
 * The height a new room starts at: the last room clicked, else the only room,
 * else the standard 9 ft.
 */
export function heightForNewRoom(plan: FloorPlan, lastClickedId?: string | null): number {
  const last = findRoom(plan, lastClickedId);
  if (last) return last.height;
  if (plan.rooms.length === 1) return plan.rooms[0].height;
  return DEFAULT_ROOM_HEIGHT_FT;
}

// ---------------------------------------------------------------------------
// Edits
// ---------------------------------------------------------------------------

const fail = (reason: PlanRefusal): PlanResult => ({ ok: false, reason });

const pairKey = (a: string, b: string) => [a, b].sort().join('|');
/** Identifies a shared wall (the pair of rooms it is between). */
export const sharedWallKey = (s: SharedStretch) => pairKey(s.owner.roomId, s.other.roomId);

const wallOf = (room: PlanRoom, face: PlanFace): Surface => room.surfaces.walls[PLAN_FACES.indexOf(face)];

/** `room` with one wall's openings replaced. Same object when nothing changed. */
function withOpenings(room: PlanRoom, face: PlanFace, openings: Opening[]): PlanRoom {
  const wall = wallOf(room, face);
  const same = (wall.openings ?? []).length === openings.length && (wall.openings ?? []).every((o, i) => o === openings[i]);
  if (same) return room;
  return {
    ...room,
    surfaces: { ...room.surfaces, walls: room.surfaces.walls.map((w, i) => (i === PLAN_FACES.indexOf(face) ? { ...w, openings } : w)) },
  };
}

/** The sheet range a wall's opening covers, low to high. */
function sheetSpan(room: PlanRect, face: PlanFace, o: { x: number; width: number }): [number, number] {
  const a = sheetAlong(room, face, o.x);
  const b = sheetAlong(room, face, o.x + o.width);
  return a <= b ? [a, b] : [b, a];
}

// ---------------------------------------------------------------------------
// Shared walls: openings in both rooms, and the cut where a wall is removed
// ---------------------------------------------------------------------------

const LINK_EPS = 0.02;

/** Where a pair of rooms' shared wall is, if they share one. */
export function stretchBetween(plan: FloorPlan, a: string, b: string): SharedStretch | undefined {
  return sharedStretches(plan).find((s) => sharedWallKey(s) === pairKey(a, b));
}

/** Is the whole shared stretch between these two rooms taken out? */
export function isWallOpen(plan: FloorPlan, a: string, b: string): boolean {
  return plan.openWalls.some((w) => pairKey(w.a, w.b) === pairKey(a, b) && w.from === undefined);
}

/** A window or door standing in a shared stretch (as the framed room has it). */
export interface StretchOpening {
  roomId: string;
  face: PlanFace;
  opening: Opening;
  /** Sheet range along the wall. */
  from: number;
  to: number;
  /** The wall behind it has been taken out. */
  wallOpen: boolean;
}

/**
 * The windows and doors standing in a shared stretch — one entry for each (a
 * linked pair is listed once, from the framed room's wall; ones with no twin
 * from whichever wall has them).
 */
export function openingsInStretch(plan: FloorPlan, stretch: SharedStretch): StretchOpening[] {
  const out: StretchOpening[] = [];
  const seen = new Set<string>();
  const sides = [
    { room: findRoom(plan, stretch.owner.roomId), face: stretch.owner.face },
    { room: findRoom(plan, stretch.other.roomId), face: stretch.other.face },
  ];
  for (const { room, face } of sides) {
    if (!room) continue;
    for (const opening of wallOf(room, face).openings ?? []) {
      if (opening.kind === 'passage') continue;
      const [from, to] = sheetSpan(room, face, opening);
      if (to <= stretch.from + 1e-6 || from >= stretch.to - 1e-6) continue;
      if (opening.link) { if (seen.has(opening.link)) continue; seen.add(opening.link); }
      const wallOpen = !!opening.link && plan.openWalls.some((w) => w.link === opening.link);
      out.push({ roomId: room.id, face, opening, from, to, wallOpen });
    }
  }
  return out.sort((p, q) => p.from - q.from);
}

/** Does any window or door stand in this sheet range (and height band) of either room's wall? */
function standsIn(plan: FloorPlan, stretch: SharedStretch, from: number, to: number, y0: number, y1: number): boolean {
  return openingsInStretch(plan, stretch).some((s) => {
    const [a, b] = sheetSpan(findRoom(plan, s.roomId)!, s.face, s.opening);
    return b > from + 1e-6 && a < to - 1e-6 && s.opening.y + s.opening.height > y0 + 1e-6 && s.opening.y < y1 - 1e-6;
  });
}

/**
 * Forget opened walls between rooms that no longer share one; unlink windows
 * and doors whose twin no longer matches (a room was moved or resized, so the
 * two are not where they were — each stays on in its own wall); and make the
 * cut where a shared wall is open, in both rooms' walls.
 */
function normalize(plan: FloorPlan): FloorPlan {
  // Every room's walls and ceiling planes agree with its ceiling profile.
  const conformed = plan.rooms.map(conformRoomToCeiling);
  if (conformed.some((r, i) => r !== plan.rooms[i])) plan = { ...plan, rooms: conformed };
  const pruned = plan.staging ? pruneStaging(plan, plan.staging) : undefined;
  if (pruned && pruned.length !== plan.staging!.length) plan = { ...plan, staging: pruned };
  const stretches = sharedStretches(plan);
  const joined = new Set(stretches.map(sharedWallKey));
  const openWalls = plan.openWalls.filter((w) => joined.has(pairKey(w.a, w.b)));
  let next: FloorPlan = openWalls.length === plan.openWalls.length ? plan : { ...plan, openWalls };

  // ---- links ----
  const linked = new Map<string, { room: PlanRoom; face: PlanFace; opening: Opening }[]>();
  for (const room of next.rooms) {
    for (const face of PLAN_FACES) {
      for (const opening of wallOf(room, face).openings ?? []) {
        if (opening.link) linked.set(opening.link, [...(linked.get(opening.link) ?? []), { room, face, opening }]);
      }
    }
  }
  const broken = new Set<string>();
  linked.forEach((ends, link) => {
    const [x, y] = ends;
    const stretch = x && y && x.room.id !== y.room.id ? stretches.find((s) => sharedWallKey(s) === pairKey(x.room.id, y.room.id)) : undefined;
    let good = ends.length === 2 && !!stretch && x.opening.kind === y.opening.kind;
    if (good && stretch) {
      const [lo, hi] = sheetSpan(x.room, x.face, x.opening);
      const [mx0, mx1] = surfaceRange(y.room, y.face, lo, hi);
      good = lo >= stretch.from - LINK_EPS && hi <= stretch.to + LINK_EPS
        && Math.abs(mx0 - y.opening.x) < LINK_EPS && Math.abs(mx1 - mx0 - y.opening.width) < LINK_EPS
        && Math.abs(x.opening.y - y.opening.y) < LINK_EPS && Math.abs(x.opening.height - y.opening.height) < LINK_EPS;
    }
    if (!good) broken.add(link);
  });
  if (broken.size > 0) {
    next = {
      ...next,
      rooms: next.rooms.map((room) => PLAN_FACES.reduce((r, face) => withOpenings(
        r, face,
        (wallOf(r, face).openings ?? []).map((o) => (o.link && broken.has(o.link) ? { ...o, link: undefined } : o)),
      ), room)),
    };
  }

  // ---- the cut where a shared wall has been removed ----
  const hasPassage = (room: PlanRoom) => room.surfaces.walls.some((w) => (w.openings ?? []).some((o) => o.kind === 'passage'));
  if (next.openWalls.length === 0 && !next.rooms.some(hasPassage)) return next;
  const planNow = next;
  const without = next.rooms.map((room) => PLAN_FACES.reduce((r, face) => {
    const list = wallOf(r, face).openings ?? [];
    return list.some((o) => o.kind === 'passage') ? withOpenings(r, face, list.filter((o) => o.kind !== 'passage')) : r;
  }, room));
  const byId = new Map(without.map((r) => [r.id, r]));
  next.openWalls.forEach((wall, index) => {
    const stretch = stretches.find((s) => sharedWallKey(s) === pairKey(wall.a, wall.b));
    const a = stretch && byId.get(stretch.owner.roomId);
    const b = stretch && byId.get(stretch.other.roomId);
    if (!stretch || !a || !b) return;
    const whole = wall.from === undefined;
    const from = whole ? stretch.from : Math.max(stretch.from, wall.from!);
    const to = whole ? stretch.to : Math.min(stretch.to, wall.to ?? stretch.to);
    if (to - from < INCH / 2) return;
    // Up to the lower ceiling: the ceiling carries on over it, the header is above it.
    const lowest = Math.min(a.height, b.height);
    const y = wall.y ?? 0;
    const height = Math.min(wall.height ?? lowest - y, lowest - y);
    if (height < 1) return;
    // The part behind a window or door that is still there is simply that opening.
    if (!whole && standsIn(planNow, stretch, from, to, y, y + height)) return;
    const add = (room: PlanRoom, face: PlanFace, other: PlanRoom, through?: number) => {
      const [x0, x1] = surfaceRange(room, face, from, to);
      byId.set(room.id, withOpenings(room, face, [
        ...(wallOf(room, face).openings ?? []),
        { id: `passage-${other.id}-${index}`, kind: 'passage', x: x0, y, width: x1 - x0, height, ...(through ? { through } : {}) },
      ]));
    };
    add(a, stretch.owner.face, b, WALL_GAP_FT);
    add(b, stretch.other.face, a);
  });
  return { ...next, rooms: next.rooms.map((r) => byId.get(r.id) ?? r) };
}

const tidy = normalize;

// ---------------------------------------------------------------------------
// Ceiling profiles
// ---------------------------------------------------------------------------

const FACE_OF: WallFaceName[] = ['front', 'right', 'back', 'left'];

/** What a ceiling plane is called in the designer, by its id. */
export function planeName(id: string): string {
  switch (id) {
    case 'ceiling-slope': return 'Ceiling';
    case 'ceiling-start': return 'Ceiling (near slope)';
    case 'ceiling-end': return 'Ceiling (far slope)';
    case 'ceiling-flat': return 'Ceiling (flat centre)';
    default: return 'Ceiling';
  }
}

/**
 * The room's wall surfaces with their outlines filled in from the ceiling
 * profile (a wall under a gable is a pentagon). Flat rooms get the walls as
 * they are. This is what the maths and the 3D view read; the stored walls are
 * plain rectangles of `width × highest point`.
 */
export function roomWallSurfaces(room: PlanRoom): Surface[] {
  if (!room.ceilingProfile || room.ceilingProfile.kind === 'flat') return room.surfaces.walls;
  const g = ceilingGeometry(room);
  return room.surfaces.walls.map((wall, i) => {
    const outline = g.wallOutline(FACE_OF[i]);
    const flat = outline.length === 4 && Math.abs(outline[2][1] - outline[3][1]) < EPS;
    return flat ? wall : { ...wall, outline };
  });
}

/**
 * The room's surfaces as the maths, the editor and the 3D view should read
 * them: walls with their outlines filled in (see `roomWallSurfaces`), the
 * ceiling or ceiling planes as stored. Edits made on these go back through
 * `withRoomSurfaces` / `applyRoomEdit`, which drop the derived outlines again.
 */
export function roomSurfacesWithOutlines(room: PlanRoom): RoomSurfaces {
  const walls = roomWallSurfaces(room);
  return walls === room.surfaces.walls ? room.surfaces : { ...room.surfaces, walls };
}

/**
 * Make a room's surfaces agree with its ceiling profile: each wall as tall as
 * its highest point (sections on the old top edge follow it — the resize rule),
 * and one ceiling surface per plane at its true sloped size, carried over from
 * the flat ceiling or the previous planes. Flat: back to the one `ceiling`.
 * Same object when nothing changes.
 */
export function conformRoomToCeiling(room: PlanRoom): PlanRoom {
  const g = ceilingGeometry(room);
  let changed = false;
  const walls = room.surfaces.walls.map((wall, i) => {
    const top = g.wallMaxTop(FACE_OF[i]);
    if (Math.abs(wall.height - top) < EPS) return wall;
    changed = true;
    return resizeSurface(wall, wall.width, top).surface;
  });
  const flat = g.planes.length === 1 && g.planes[0].rise < EPS;
  let ceiling = room.surfaces.ceiling;
  let planes = room.surfaces.ceilingPlanes;
  if (flat) {
    if (planes) {
      // Back to one flat ceiling: the largest plane's layout, resized.
      const source = Object.values(planes).sort((a, b) => b.width * b.height - a.width * a.height)[0];
      ceiling = source ? { ...resizeSurface(source, room.length, room.width).surface, id: 'ceiling', name: 'Ceiling', kind: 'ceiling', outline: undefined } : ceiling;
      planes = undefined;
      changed = true;
    }
    if (ceiling && (Math.abs(ceiling.width - room.length) > EPS || Math.abs(ceiling.height - room.width) > EPS)) {
      ceiling = resizeSurface(ceiling, room.length, room.width).surface;
      changed = true;
    }
  } else if (ceiling || planes) {
    // A profile: one surface per plane. An existing plane of the same id is
    // resized; a new one takes the flat ceiling's (or any plane's) layout.
    const source = ceiling ?? Object.values(planes ?? {})[0];
    const next: Record<string, Surface> = {};
    for (const p of g.planes) {
      const have = planes?.[p.id];
      if (have && Math.abs(have.width - p.width) < EPS && Math.abs(have.height - p.height) < EPS) { next[p.id] = have; continue; }
      const from = have ?? source;
      next[p.id] = from
        ? { ...resizeSurface(from, p.width, p.height).surface, id: p.id, name: planeName(p.id), kind: 'ceiling', outline: undefined }
        : { id: p.id, name: 'Ceiling', kind: 'ceiling', width: p.width, height: p.height, sections: [] };
      changed = true;
    }
    if (planes && Object.keys(planes).some((id) => !next[id])) changed = true;
    planes = next;
    if (ceiling) { ceiling = undefined; changed = true; }
  }
  if (!changed) return room;
  const surfaces: RoomSurfaces = { ...room.surfaces, walls };
  if (ceiling) surfaces.ceiling = ceiling; else delete surfaces.ceiling;
  if (planes) surfaces.ceilingPlanes = planes; else delete surfaces.ceilingPlanes;
  return { ...room, surfaces };
}

/**
 * A room's surfaces resized to the room's size and ceiling geometry: each wall
 * to its own highest point (not the eave — a gable wall's sections above the
 * eave must survive a change of size), the ceiling planes by `conformRoomToCeiling`.
 */
function resizeRoomSurfaces(room: PlanRoom): RoomSurfaces {
  const g = ceilingGeometry(room);
  const walls = room.surfaces.walls.map((wall, i) => resizeSurface(wall, i % 2 === 0 ? room.length : room.width, g.wallMaxTop(FACE_OF[i])).surface);
  return conformRoomToCeiling({ ...room, surfaces: { ...room.surfaces, walls } }).surfaces;
}

/**
 * Copy one room's ceiling to others: its shape (the profile — refused where a
 * target would end up over the height limit) and what is laid on it, plane for
 * plane (a flat ceiling onto a flat one; a slope onto the matching slope, or
 * failing that whatever the source has), each layout stretched to the target
 * surface the way a copied wall layout is. The targets' wall heights and
 * everything on their walls are left alone.
 */
export function copyCeiling(plan: FloorPlan, fromId: string, toIds: string[]): PlanResult {
  const from = findRoom(plan, fromId);
  if (!from) return fail('not-found');
  const targets = toIds.filter((id) => id !== fromId);
  if (targets.some((id) => !findRoom(plan, id))) return fail('not-found');
  const shaped = setCeilingProfile(plan, targets, from.ceilingProfile ?? FLAT);
  if (!shaped.ok) return shaped;
  const sources: Surface[] = [...(from.surfaces.ceiling ? [from.surfaces.ceiling] : []), ...Object.values(from.surfaces.ceilingPlanes ?? {})];
  if (sources.length === 0) return shaped;
  const sourceFor = (id: string): Surface => from.surfaces.ceilingPlanes?.[id] ?? from.surfaces.ceiling ?? sources[0];
  const laid = (to: Surface, source: Surface): Surface => ({ ...to, sections: copySectionsTo(source, to) });
  const rooms = shaped.plan.rooms.map((room) => {
    if (!targets.includes(room.id)) return room;
    const surfaces: RoomSurfaces = { ...room.surfaces };
    if (surfaces.ceiling) surfaces.ceiling = laid(surfaces.ceiling, sourceFor('ceiling'));
    if (surfaces.ceilingPlanes) {
      surfaces.ceilingPlanes = Object.fromEntries(Object.entries(surfaces.ceilingPlanes).map(([id, plane]) => [id, laid(plane, sourceFor(id))]));
    }
    return { ...room, surfaces };
  });
  return { ok: true, plan: tidy({ ...shaped.plan, rooms }) };
}

/** Give rooms a ceiling profile (flat = `{ kind: 'flat' }`); their walls and ceilings follow. */
export function setCeilingProfile(plan: FloorPlan, ids: string[], profile: CeilingProfile): PlanResult {
  if (!ids.every((id) => findRoom(plan, id))) return fail('not-found');
  for (const id of ids) {
    const room = findRoom(plan, id)!;
    const issue = profileIssue(room, profile, ROOM_LIMITS.height.max);
    if (issue === 'too-tall') return fail('too-large');
    if (issue) return fail('bad-profile');
  }
  return {
    ok: true,
    plan: tidy({
      ...plan,
      rooms: plan.rooms.map((r) => (ids.includes(r.id)
        ? (profile.kind === 'flat' ? (({ ceilingProfile: _drop, ...rest }) => rest)(r) : { ...r, ceilingProfile: profile })
        : r)),
    }),
  };
}

/** Is a wall of this room shared with another and taken out? */
export function wallIsGone(plan: FloorPlan, room: PlanRoom, face: PlanFace): boolean {
  return sharedStretches(plan).some((s) =>
    ((s.owner.roomId === room.id && s.owner.face === face) || (s.other.roomId === room.id && s.other.face === face))
    && isWallOpen(plan, s.owner.roomId, s.other.roomId));
}

/**
 * Keep only the staged props that still make sense on this plan: their room
 * exists, a floor prop is inside it, a hung prop's wall still stands and (for
 * SlatWall props) its host section is still there.
 */
export function pruneStaging(plan: FloorPlan, staged: StagedProp[]): StagedProp[] {
  const kept = staged.filter((s) => {
    const room = findRoom(plan, s.roomId);
    if (!room) return false;
    if (s.kind === 'floor') {
      // In a garage door's bay (part of it may stand outside): kept while that door is there.
      if (s.bay) {
        const { face, openingId } = s.bay;
        return !wallIsGone(plan, room, face) && (wallOf(room, face).openings ?? []).some((o) => o.id === openingId && o.kind === 'garageDoor');
      }
      // Else in the room when its BOX is (its origin may sit behind the wall: `body`).
      const b = s.body ?? s;
      return b.x >= 0 && b.x <= room.length && b.z >= 0 && b.z <= room.width;
    }
    if (s.kind === 'yard') {
      // Outside the house: kept while its room is there, it is on the lawn and off every room.
      const b = s.body ?? s;
      const x = room.x + b.x, z = room.z + b.z;
      const bounds = planBounds(plan)!;
      const reach = LAWN_MARGIN_FT + WALL_GAP_FT + 1;
      if (x < bounds.x - reach || x > bounds.x + bounds.length + reach || z < bounds.z - reach || z > bounds.z + bounds.width + reach) return false;
      return !plan.rooms.some((r) => x > r.x - WALL_GAP_FT && x < r.x + r.length + WALL_GAP_FT && z > r.z - WALL_GAP_FT && z < r.z + r.width + WALL_GAP_FT);
    }
    if (!s.face) return false;
    const wall = wallOf(room, s.face);
    if (s.x < 0 || s.x > wall.width || (s.y ?? 0) > wall.height) return false;
    if (wallIsGone(plan, room, s.face)) return false;
    if ((s.kind === 'slatwall' || s.kind === 'accessory') && s.hostSectionId && !wall.sections.some((sec) => sec.id === s.hostSectionId)) return false;
    return true;
  });
  // A hung prop goes with the accessory holding it: a staged one that was
  // pruned above, or the user's, which may have been taken off the wall.
  const stagedAccessories = new Set(kept.filter((s) => s.kind === 'accessory').map((s) => s.id));
  const userAccessories = new Set(plan.rooms.flatMap((r) => r.surfaces.walls.flatMap((w) => w.sections.flatMap((sec) => (sec.accessories ?? []).map((a) => a.id)))));
  return kept.filter((s) => s.kind !== 'slatwall' || !s.hostAccessoryId || stagedAccessories.has(s.hostAccessoryId) || userAccessories.has(s.hostAccessoryId));
}

/** The plan with this staging (replacing any), tidied. */
export function withStaging(plan: FloorPlan, staging: StagedProp[] | undefined, spaceType?: string): FloorPlan {
  const next: FloorPlan = { ...plan, staging: staging && staging.length > 0 ? staging : undefined };
  if (spaceType !== undefined) next.spaceType = spaceType;
  if (!next.staging) delete next.finish;
  return normalize(next);
}

/** The plan with what Stage drew for the render (renderFinish.ts pickFinish), or without it. */
export function withFinish(plan: FloorPlan, finish: PlanFinish | undefined): FloorPlan {
  const next: FloorPlan = { ...plan };
  if (finish) next.finish = finish; else delete next.finish;
  return next;
}

/** Is the shared wall between these two rooms taken out? */
export function toggleOpenWall(plan: FloorPlan, a: string, b: string): PlanResult {
  const stretch = stretchBetween(plan, a, b);
  if (!stretch) return fail('not-found');
  if (isWallOpen(plan, a, b)) {
    return { ok: true, plan: normalize({ ...plan, openWalls: plan.openWalls.filter((w) => !(pairKey(w.a, w.b) === pairKey(a, b) && w.from === undefined)) }) };
  }
  const owner = findRoom(plan, stretch.owner.roomId)!;
  const other = findRoom(plan, stretch.other.roomId)!;
  if (Math.min(owner.height, other.height) < 1) return fail('too-small');
  // Whatever stood in that stretch goes with the wall — in both rooms.
  const clear = (room: PlanRoom, face: PlanFace, from: number, to: number) => {
    const list = wallOf(room, face).openings ?? [];
    return withOpenings(room, face, list.filter((o) => o.x + o.width <= from + 1e-6 || o.x >= to - 1e-6));
  };
  return {
    ok: true,
    plan: normalize({
      ...plan,
      rooms: plan.rooms.map((r) => (r.id === owner.id
        ? clear(r, stretch.owner.face, stretch.owner.from, stretch.owner.to)
        : r.id === other.id ? clear(r, stretch.other.face, stretch.other.from, stretch.other.to) : r)),
      // (Any part already taken out behind a window or door is part of this now.)
      openWalls: [...plan.openWalls.filter((w) => pairKey(w.a, w.b) !== pairKey(a, b)), { a, b }],
    }),
  };
}

/**
 * Take the wall out behind ONE window or door of a shared stretch, and leave
 * the window or door where it is: its trim covers the opening in both rooms.
 * If it is deleted later, a framed void is what is left (see `voidsBehind`).
 */
export function openWallBehind(plan: FloorPlan, roomId: string, openingId: string): PlanResult {
  const room = findRoom(plan, roomId);
  const mine = room?.surfaces.walls.flatMap((w) => w.openings ?? []).find((o) => o.id === openingId);
  if (!room || !mine) return fail('not-found');
  if (!mine.link) {
    // On the wall from before the rooms were joined, so in this room only: put
    // its twin in the other room's wall first (as if it had just been placed).
    const linked = applyRoomEdit(plan, roomId, room.surfaces);
    if (!linked.ok) return linked;
    const now = findRoom(linked.plan, roomId)?.surfaces.walls.flatMap((w) => w.openings ?? []).find((o) => o.id === openingId);
    if (!now?.link) return fail('not-found');
    return openWallBehind(linked.plan, roomId, openingId);
  }
  for (const s of sharedStretches(plan)) {
    // (Listed from the framed room's wall, whichever room it was picked in.)
    const found = openingsInStretch(plan, s).find((e) => (mine.link ? e.opening.link === mine.link : e.opening.id === openingId && e.roomId === roomId));
    if (!found) continue;
    const link = found.opening.link;
    if (!link) return fail('not-found');
    if (plan.openWalls.some((w) => w.link === link)) return { ok: true, plan };
    const lowest = Math.min(findRoom(plan, s.owner.roomId)!.height, findRoom(plan, s.other.roomId)!.height);
    const y = found.opening.y;
    if (lowest - y < 1) return fail('too-small');
    // Whatever whole-wall state there was, this part is open now.
    const wall: OpenWall = {
      a: s.owner.roomId, b: s.other.roomId, link,
      from: Math.max(s.from, found.from), to: Math.min(s.to, found.to),
      y, height: Math.min(found.opening.height, lowest - y),
    };
    return { ok: true, plan: normalize({ ...plan, openWalls: [...plan.openWalls, wall] }) };
  }
  return fail('not-found');
}

/** Put the wall back behind a window or door (by its link). */
export function closeWallBehind(plan: FloorPlan, link: string): FloorPlan {
  return normalize({ ...plan, openWalls: plan.openWalls.filter((w) => w.link !== link) });
}

/**
 * The links of the windows and doors among `openingIds` (of one room) that have
 * the wall taken out behind them — deleting one leaves a void.
 */
export function voidsBehind(plan: FloorPlan, roomId: string, openingIds: string[]): string[] {
  const room = findRoom(plan, roomId);
  if (!room) return [];
  const links = new Set<string>();
  for (const wall of room.surfaces.walls) {
    for (const o of wall.openings ?? []) {
      if (openingIds.includes(o.id) && o.link && plan.openWalls.some((w) => w.link === o.link)) links.add(o.link);
    }
  }
  return [...links];
}

/**
 * A room's edited surfaces go into the plan — and what the edit did to a window
 * or door on a wall shared with another room is carried across: one that sits
 * wholly inside the shared stretch is in BOTH rooms (a copy is made, kept alike
 * from then on); one moved wholly out of it, or deleted, leaves the other room
 * too. One that would straddle the end of the stretch is refused, as is one the
 * other room's wall has no place for.
 */
export function applyRoomEdit(plan: FloorPlan, roomId: string, surfaces: RoomSurfaces, margin = 0): PlanResult {
  const before = findRoom(plan, roomId);
  if (!before) return fail('not-found');
  let next = withRoomSurfaces(plan, roomId, surfaces);
  const stretches = sharedStretches(next);
  if (stretches.length === 0 && !next.rooms.some((r) => r.surfaces.walls.some((w) => (w.openings ?? []).some((o) => o.link)))) {
    return { ok: true, plan: normalize(next) };
  }

  let serial = 0;
  for (const room of next.rooms) for (const w of room.surfaces.walls) for (const o of w.openings ?? []) {
    serial = Math.max(serial, Number(/^lk(\d+)$/.exec(o.link ?? '')?.[1] ?? 0));
  }
  const newLink = () => `lk${++serial}`;
  const edit = (id: string, face: PlanFace, change: (list: Opening[]) => Opening[]) => {
    next = { ...next, rooms: next.rooms.map((r) => (r.id === id ? withOpenings(r, face, change(wallOf(r, face).openings ?? [])) : r)) };
  };
  const dropCopies = (link: string) => {
    for (const r of next.rooms) {
      if (r.id === roomId) continue;
      for (const face of PLAN_FACES) {
        if ((wallOf(r, face).openings ?? []).some((o) => o.link === link)) edit(r.id, face, (l) => l.filter((o) => o.link !== link));
      }
    }
  };

  for (const face of PLAN_FACES) {
    const me = findRoom(next, roomId)!;
    const wall = wallOf(me, face);
    const old = wallOf(before, face).openings ?? [];
    const now = wall.openings ?? [];
    // Linked openings that were here and are gone: their copies go too.
    for (const o of old) if (o.link && !now.some((n) => n.link === o.link)) dropCopies(o.link);

    const here = stretches.filter((s) => (s.owner.roomId === roomId && s.owner.face === face) || (s.other.roomId === roomId && s.other.face === face));
    for (const added of now) {
      if (added.kind === 'passage') continue;
      let o = added;
      let [lo, hi] = sheetSpan(me, face, o);
      const within = (s: SharedStretch) => lo >= s.from - 1e-6 && hi <= s.to + 1e-6;
      let inside = here.find(within);
      const overlaps = () => here.some((s) => hi > s.from + 1e-6 && lo < s.to - 1e-6);
      if (overlaps() && !inside) {
        // One just added (the "add a window" button puts it at the middle of the
        // wall) slides to the nearest place that does not straddle; one that was
        // dragged there is refused.
        const slid = old.some((x) => x.id === o.id) ? null : slideClear(wall, o, here.map((s) => (s.owner.roomId === roomId ? s.owner : s.other)), margin);
        if (!slid) return fail('straddles-shared-wall');
        edit(roomId, face, (l) => l.map((x) => (x === added ? slid : x)));
        o = slid;
        [lo, hi] = sheetSpan(me, face, o);
        inside = here.find(within);
      }
      if (!inside) {
        // Not on a shared stretch (any more): it is this room's alone.
        if (o.link) {
          dropCopies(o.link);
          edit(roomId, face, (l) => l.map((x) => (x === o ? { ...x, link: undefined } : x)));
        }
        continue;
      }
      const mineIsOwner = inside.owner.roomId === roomId;
      const partner = findRoom(next, mineIsOwner ? inside.other.roomId : inside.owner.roomId)!;
      const partnerFace = mineIsOwner ? inside.other.face : inside.owner.face;
      const link = o.link ?? newLink();
      const [px0, px1] = surfaceRange(partner, partnerFace, lo, hi);
      const rect = { x: px0, y: o.y, width: px1 - px0, height: o.height };
      const partnerWall = wallOf(partner, partnerFace);
      const copy = (partnerWall.openings ?? []).find((x) => x.link === link);
      const issue = checkOpeningPlacement(partnerWall, rect, o, { margin, ignoreId: copy?.id });
      if (issue) return fail('other-room');
      const twin: Opening = {
        ...(copy ?? {}),
        id: copy?.id ?? nextOpeningId(partnerWall, o.kind),
        kind: o.kind, ...rect, style: o.style, floorPinned: o.floorPinned, link,
        // Hinges on the same side of the doorway, opening the same way — which,
        // seen from the other room, is the other side and the other way.
        ...(o.kind === 'door' ? mirroredHanding(o) : {}),
      };
      edit(partner.id, partnerFace, (l) => (copy ? l.map((x) => (x === copy ? twin : x)) : [...l, twin]));
      if (!o.link) edit(roomId, face, (l) => l.map((x) => (x === o ? { ...x, link } : x)));
    }
  }
  return { ok: true, plan: normalize(next) };
}

/**
 * The nearest place, along `wall`, for a new `o` that is wholly inside a shared
 * stretch or wholly clear of every one (ranges in the wall's own x) — or null.
 */
function slideClear(wall: Surface, o: Opening, stretches: { from: number; to: number }[], margin: number): Opening | null {
  const tries: number[] = [];
  for (const s of stretches) {
    // Inside, a safe margin in from each end (the other room's wall may end there).
    if (s.to - s.from >= o.width + 2 * margin) tries.push(Math.min(Math.max(o.x, s.from + margin), s.to - margin - o.width));
    tries.push(s.from - o.width, s.to);
  }
  let best: Opening | null = null;
  for (const x of tries) {
    const rect = { x, y: o.y, width: o.width, height: o.height };
    if (checkOpeningPlacement(wall, rect, o, { margin, ignoreId: o.id })) continue;
    const clear = stretches.every((s) => x + o.width <= s.from + 1e-6 || x >= s.to - 1e-6 || (x >= s.from - 1e-6 && x + o.width <= s.to + 1e-6));
    if (clear && (!best || Math.abs(x - o.x) < Math.abs(best.x - o.x))) best = { ...o, x };
  }
  return best;
}

const others = (plan: FloorPlan, ids: string[]): PlanRoom[] => plan.rooms.filter((r) => !ids.includes(r.id));

export type AddRoomResult = { ok: true; plan: FloorPlan; roomId: string } | { ok: false; reason: PlanIssue };

/** A new bare room. It goes last, so any room it joins owns the wall between them. */
export function addRoom(plan: FloorPlan, rect: PlanRect, height: number, name?: string): AddRoomResult {
  const issue = placementIssue(plan.rooms, rect);
  if (issue) return { ok: false, reason: issue };
  const n = nextNumber(plan);
  const dims = { length: rect.length, width: rect.width, height };
  const room: PlanRoom = { id: `room${n}`, name: name ?? `Room ${n}`, ...rectOfRoom(rect), height, surfaces: bareSurfaces(dims) };
  return { ok: true, plan: { ...plan, rooms: [...plan.rooms, room] }, roomId: room.id };
}

/**
 * Move and / or resize one room. What is on its walls follows a change of size
 * by the designer's resize rule (`resizeRoom`): edges on a wall's boundary stay
 * on it, everything else keeps its place.
 */
export function setRoomRect(plan: FloorPlan, id: string, rect: PlanRect): PlanResult {
  const room = findRoom(plan, id);
  if (!room) return fail('not-found');
  const issue = placementIssue(others(plan, [id]), rect);
  if (issue) return fail(issue);
  const resized = Math.abs(rect.length - room.length) > EPS || Math.abs(rect.width - room.width) > EPS;
  const next: PlanRoom = {
    ...room,
    ...rectOfRoom(rect),
    surfaces: resized ? resizeRoomSurfaces({ ...room, ...rectOfRoom(rect) }) : room.surfaces,
  };
  return { ok: true, plan: tidy({ ...plan, rooms: plan.rooms.map((r) => (r.id === id ? next : r)) }) };
}

/** Shift several rooms together; refused if any of them would end up somewhere it may not be. */
export function moveRooms(plan: FloorPlan, ids: string[], dx: number, dz: number): PlanResult {
  const moving = plan.rooms.filter((r) => ids.includes(r.id));
  if (moving.length === 0 || moving.length !== ids.length) return fail('not-found');
  const still = others(plan, ids);
  for (const room of moving) {
    const issue = placementIssue(still, { ...rectOfRoom(room), x: room.x + dx, z: room.z + dz });
    if (issue) return fail(issue);
  }
  return {
    ok: true,
    plan: tidy({ ...plan, rooms: plan.rooms.map((r) => (ids.includes(r.id) ? { ...r, x: r.x + dx, z: r.z + dz } : r)) }),
  };
}

/**
 * Arrow-key nudge: move the rooms one step, or as far short of it as there is
 * room — flush against (joined to) whatever is in the way.
 */
export function nudgeRooms(plan: FloorPlan, ids: string[], dx: number, dz: number): PlanResult {
  const moving = plan.rooms.filter((r) => ids.includes(r.id));
  if (moving.length === 0) return fail('not-found');
  const alongX = dx !== 0;
  const step = Math.abs(alongX ? dx : dz);
  const sign = Math.sign(alongX ? dx : dz);
  const tries = new Set<number>([step]);
  for (const m of moving) {
    for (const o of others(plan, ids)) {
      const gap = alongX
        ? (sign > 0 ? o.x - (m.x + m.length) : m.x - (o.x + o.length))
        : (sign > 0 ? o.z - (m.z + m.width) : m.z - (o.z + o.width));
      const join = gap - WALL_GAP_FT;
      if (join > EPS && join < step) tries.add(join);
    }
  }
  for (let d = step - INCH; d > EPS; d -= INCH) tries.add(d);
  let refused: PlanResult = fail('overlap');
  for (const distance of [...tries].sort((a, b) => b - a)) {
    const result = moveRooms(plan, ids, alongX ? sign * distance : 0, alongX ? 0 : sign * distance);
    if (result.ok) return result;
    if (distance === step) refused = result;
  }
  return refused;
}

/** Give rooms a new ceiling height; their walls' layouts follow. */
export function setRoomsHeight(plan: FloorPlan, ids: string[], height: number): PlanResult {
  const { min, max } = ROOM_LIMITS.height;
  if (height < min - EPS) return fail('too-small');
  if (height > max + EPS) return fail('too-large');
  if (!ids.every((id) => findRoom(plan, id))) return fail('not-found');
  return {
    ok: true,
    // (A removed shared wall is cut to the lower ceiling, so it follows.)
    plan: tidy({
      ...plan,
      rooms: plan.rooms.map((r) => (ids.includes(r.id) && Math.abs(r.height - height) > EPS
        ? { ...r, height, surfaces: resizeRoomSurfaces({ ...r, height }) }
        : r)),
    }),
  };
}

export function renameRoom(plan: FloorPlan, id: string, name: string): PlanResult {
  if (!findRoom(plan, id)) return fail('not-found');
  return { ok: true, plan: { ...plan, rooms: plan.rooms.map((r) => (r.id === id ? { ...r, name } : r)) } };
}

export function deleteRooms(plan: FloorPlan, ids: string[]): PlanResult {
  if (!ids.every((id) => findRoom(plan, id))) return fail('not-found');
  return { ok: true, plan: tidy({ ...plan, rooms: others(plan, ids) }) };
}

/** Put a room's edited surfaces back into the plan (without any derived wall outlines — those are read-time). */
export function withRoomSurfaces(plan: FloorPlan, id: string, surfaces: RoomSurfaces): FloorPlan {
  const stored: RoomSurfaces = surfaces.walls.some((w) => w.outline)
    ? { ...surfaces, walls: surfaces.walls.map((w) => (w.outline ? (({ outline: _o, ...rest }) => rest)(w) : w)) }
    : surfaces;
  return { ...plan, rooms: plan.rooms.map((r) => (r.id === id ? { ...r, surfaces: stored } : r)) };
}
